import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fields we expose in the editor per slide type. Kept intentionally
 * narrow — structural fields (`type`, `is_scene`, `act`, `scene_id`)
 * are NOT editable, only content.
 */
const FIELDS_BY_TYPE = {
  title: ["logo_url", "logo_alt", "tagline"],
  intro: ["act_label", "headline", "subhead"],
  scene: ["scene_label", "headline", "subhead"],
  qr: ["scene_label", "headline", "subhead", "qr_instructions"],
  app: ["scene_label"],
  recap: ["scene_label", "headline", "subhead"],
  conclusion: ["headline", "subhead"],
};

// Shown on every slide card, always last. Never rendered on the
// projector — the moderator console surfaces it for Dan.
const SPEAKER_NOTES_FIELD = "speaker_notes";

/** Extra fields that only exist on specific slides. */
const EXTRA_FIELDS_BY_KEY = {
  "act1-scene2": ["stoplight_red", "stoplight_yellow", "stoplight_green"],
  "act2-scene2": ["shuffle_image_url"],
  "act2-scene1": [
    "wheel_song_1",
    "wheel_song_2",
    "wheel_song_3",
    "wheel_song_4",
    "wheel_song_5",
    "wheel_song_6",
    "wheel_song_7",
  ],
};

/**
 * Compact slide editor. `scope="template"` edits the master template
 * (which becomes the default for every new event). `scope="event"` edits
 * a specific event's snapshot. Both use the same rendering + save
 * logic; only the endpoints differ.
 */
export default function SlideEditor({ scope, slug, onClose }) {
  const auth = useAuth();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [dirty, setDirty] = useState({}); // key -> partial edits
  // Event-scope only: show-wide details reused across slides + the recap.
  const [details, setDetails] = useState({ location_name: "", parsha_name: "" });
  const [savedDetails, setSavedDetails] = useState({ location_name: "", parsha_name: "" });
  const [savingDetails, setSavingDetails] = useState(false);

  const ax = useMemo(() => {
    const inst = axios.create({ baseURL: API, withCredentials: true });
    if (auth.token) inst.defaults.headers.common.Authorization = `Bearer ${auth.token}`;
    return inst;
  }, [auth.token]);

  const fetchUrl = scope === "template" ? "/admin/live/slide-template" : `/live/${slug}`;
  const putUrl =
    scope === "template" ? "/admin/live/slide-template" : `/admin/live/events/${slug}/slides`;

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await ax.get(fetchUrl);
        if (!cancel) {
          setSlides(data.slides || []);
          if (scope !== "template") {
            const d = {
              location_name: data.location_name || "",
              parsha_name: data.parsha_name || "",
            };
            setDetails(d);
            setSavedDetails(d);
          }
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load slides.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [ax, fetchUrl, scope]);

  const detailsDirty =
    details.location_name.trim() !== savedDetails.location_name ||
    details.parsha_name.trim() !== savedDetails.parsha_name;

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      const { data } = await ax.patch(`/admin/live/events/${slug}`, {
        location_name: details.location_name.trim(),
        parsha_name: details.parsha_name.trim(),
      });
      const d = { location_name: data.location_name || "", parsha_name: data.parsha_name || "" };
      setDetails(d);
      setSavedDetails(d);
      toast.success("Show details saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save show details.");
    } finally {
      setSavingDetails(false);
    }
  };

  const editField = (slideKey, field, value) => {
    setDirty((d) => ({
      ...d,
      [slideKey]: { ...(d[slideKey] || {}), [field]: value },
    }));
  };

  const saveSlide = async (slideKey) => {
    const patch = dirty[slideKey];
    if (!patch) return;
    setSavingKey(slideKey);
    try {
      const method = scope === "template" ? "put" : "patch";
      const { data } = await ax[method](putUrl, { slides: [{ key: slideKey, ...patch }] });
      setSlides(data.slides || []);
      setDirty((d) => {
        const { [slideKey]: _drop, ...rest } = d;
        return rest;
      });
      toast.success("Slide saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save slide.");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="text-white/60 py-16 text-center">Loading slides…</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto py-6 px-4" data-testid={`slide-editor-${scope}`}>
      <div className="flex items-center justify-between mb-6">
        <h2
          className="text-2xl"
          style={{ fontFamily: "'Frank Ruhl Libre', serif", color: "#F5EFE3" }}
        >
          {scope === "template" ? "Master template" : "This event's slides"}
        </h2>
        {onClose ? (
          <Button
            variant="outline"
            onClick={onClose}
            className="h-9 rounded-full text-xs border-white/20"
          >
            Close
          </Button>
        ) : null}
      </div>
      <div className="space-y-3">
        {scope !== "template" ? (
          <div
            className="rounded-2xl border px-5 py-4"
            style={{ borderColor: "rgba(167,116,255,0.3)", backgroundColor: "rgba(167,116,255,0.05)" }}
            data-testid="slide-editor-show-details"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "rgba(245,239,227,0.5)" }}>
              Show details · used on the Act 3 Scene 1 staff + the recap collage
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: "rgba(245,239,227,0.5)" }}>
                  location
                </div>
                <Input
                  value={details.location_name}
                  onChange={(e) => setDetails((d) => ({ ...d, location_name: e.target.value }))}
                  placeholder="e.g. Las Vegas"
                  data-testid="slide-editor-location"
                  className="h-9 text-sm"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#F5EFE3",
                  }}
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: "rgba(245,239,227,0.5)" }}>
                  parsha (hebrew)
                </div>
                <Input
                  value={details.parsha_name}
                  onChange={(e) => setDetails((d) => ({ ...d, parsha_name: e.target.value }))}
                  placeholder="בראשית"
                  dir="rtl"
                  data-testid="slide-editor-parsha"
                  className="h-9 text-sm"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#F5EFE3",
                  }}
                />
              </label>
              <Button
                type="button"
                disabled={!detailsDirty || savingDetails}
                onClick={saveDetails}
                data-testid="slide-editor-details-save"
                className="h-9 rounded-full text-xs"
                style={{
                  backgroundColor: detailsDirty ? "#A774FF" : "rgba(255,255,255,0.06)",
                  color: detailsDirty ? "#241246" : "rgba(245,239,227,0.5)",
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              >
                {savingDetails ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        ) : null}
        {slides.map((s) => {
          const fields = [
            ...(FIELDS_BY_TYPE[s.type] || []),
            ...(EXTRA_FIELDS_BY_KEY[s.key] || []),
            SPEAKER_NOTES_FIELD,
          ];
          const edits = dirty[s.key] || {};
          const hasEdits = Object.keys(edits).length > 0;
          return (
            <div
              key={s.key}
              className="rounded-2xl border px-5 py-4"
              style={{ borderColor: "rgba(232,196,138,0.24)", backgroundColor: "rgba(232,196,138,0.03)" }}
              data-testid={`slide-editor-row-${s.key}`}
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div
                    className="text-[10px] uppercase tracking-[0.32em]"
                    style={{ color: "rgba(245,239,227,0.5)" }}
                  >
                    {s.type} · {s.key}
                    {s.is_scene ? ` · Act ${s.act ?? "-"}` : ""}
                  </div>
                  <div className="text-sm truncate" style={{ color: "#F5EFE3" }}>
                    {edits.headline ?? s.headline ?? edits.tagline ?? s.tagline ?? "—"}
                  </div>
                </div>
                <Button
                  type="button"
                  disabled={!hasEdits || savingKey === s.key}
                  onClick={() => saveSlide(s.key)}
                  className="h-9 rounded-full text-xs"
                  data-testid={`slide-save-${s.key}`}
                  style={{
                    backgroundColor: hasEdits ? "#F2D589" : "rgba(255,255,255,0.06)",
                    color: hasEdits ? "#080419" : "rgba(245,239,227,0.5)",
                    fontWeight: 600,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                  }}
                >
                  {savingKey === s.key ? "Saving…" : "Save"}
                </Button>
              </div>
              <div className="grid gap-2">
                {fields.map((f) => (
                  <label key={f} className="block">
                    <div
                      className="text-[10px] uppercase tracking-[0.28em] mb-1"
                      style={{ color: "rgba(245,239,227,0.5)" }}
                    >
                      {f}
                    </div>
                    {f === "subhead" || f === SPEAKER_NOTES_FIELD ? (
                      <textarea
                        value={edits[f] ?? s[f] ?? ""}
                        onChange={(e) => editField(s.key, f, e.target.value)}
                        rows={f === SPEAKER_NOTES_FIELD ? 4 : 2}
                        data-testid={`slide-field-${s.key}-${f}`}
                        className="w-full rounded-md px-3 py-2 text-sm"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "#F5EFE3",
                          resize: "vertical",
                        }}
                      />
                    ) : (
                      <Input
                        value={edits[f] ?? s[f] ?? ""}
                        onChange={(e) => editField(s.key, f, e.target.value)}
                        data-testid={`slide-field-${s.key}-${f}`}
                        className="h-9 text-sm"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "#F5EFE3",
                        }}
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
